import React, { useCallback, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { AutocompleteRenderInputParams, AutocompleteRenderOptionState, Box, Checkbox, MenuItem } from '@mui/material';

import styled from 'styled-components';

import { CenteredCircularProgress } from 'components/Progress';

import { useInfiniteSearch } from 'utils/hooks/infiniteSearch';

import { StyledTextField } from 'components/Input/styles';
import InfiniteAutocomplete from 'components/InfiniteAutocomplete';
import { limitChips } from 'components/Input/limitChips';
import API from 'utils/api';
import { SearchRequest, SearchResponse, UuidableName } from 'utils/api/types';
import Chip from 'components/Chip';
import { getEntityTranslation } from 'stores/EntityDetails/utils';
import { EntityModules } from 'core/constants';

const loadingOption = { uuid: 'loading', name: 'Loading...' } as SearchResponse;
const nullOption = { uuid: 'null', name: '' } as SearchResponse;

const StyledMenuItem = styled(MenuItem)`
  color: ${({ theme }) => theme.palette.text.primary};
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 143%;
  letter-spacing: 0.17px;
  width: 100%;
`;

const EntitySearchField: React.FunctionComponent<{
  field: string;
  updateField: (field: string, value: UuidableName | UuidableName[] | null) => void;
  data: UuidableName | UuidableName[] | null;
  module: string;
  multiple?: boolean;
  maxChips?: number;
  error?: boolean;
  extraParams?: { published_only?: boolean; task_linkable_only?: boolean };
}> = ({ field, updateField, data, module, multiple = false, maxChips, error, extraParams }): React.ReactElement => {
  const params = useParams<{ organisationId: string; publicFormId: string }>();

  const { singularKey } = getEntityTranslation[module as EntityModules];

  const { organisationId } = params;
  // public form id for entities submission
  const { publicFormId } = params;

  const request = useCallback(
    (searchParams?: SearchRequest) =>
      API.entities.searchEntity(module, organisationId, {
        ...searchParams,
        ...extraParams,
        public_uuid: publicFormId,
        ordering: `${module}__name`,
      }),
    [extraParams, module, organisationId, publicFormId]
  );

  const { results, loading, loadingMore, hasMore, loadMore, searchQuery } = useInfiniteSearch(request);

  // Handle state based on multiple selection
  const [selectedEntity, setSelectedEntity] = React.useState<UuidableName | UuidableName[] | null | undefined>(data);

  // Update selected entity when data changes
  useEffect(() => {
    if (!publicFormId) {
      setSelectedEntity(data);
    } else if (typeof data === 'string') {
      setSelectedEntity(results.find((result) => result.uuid === data) || nullOption);
    } else if (data) {
      setSelectedEntity(data);
    }
  }, [data, publicFormId, results]);

  const onChangeSearch = (s: string): void => {
    searchQuery(s);
  };

  const handleRenderOption = useCallback(
    (
      props: React.HTMLAttributes<HTMLLIElement>,
      option: UuidableName,
      { selected }: AutocompleteRenderOptionState
    ): React.ReactNode => {
      const { uuid, name } = option;
      // Extract key from props to avoid spreading it - React keys must be passed directly
      // Always use uuid for key to ensure uniqueness (Autocomplete may generate duplicate keys from names)
      const { key: _key, ...restProps } = props as React.HTMLAttributes<HTMLLIElement> & { key?: React.Key };

      return uuid === loadingOption.uuid ? (
        <CenteredCircularProgress key={uuid} />
      ) : (
        <Box key={uuid} sx={{ alignItems: 'center' }}>
          <StyledMenuItem {...restProps} value={uuid}>
            {multiple && <Checkbox checked={selected} />}
            {name}
          </StyledMenuItem>
        </Box>
      );
    },
    [multiple]
  );

  const handleRenderInput = useMemo(
    () => (inputParams: AutocompleteRenderInputParams) => {
      const { InputProps, ...restParams } = inputParams;

      return (
        <StyledTextField
          {...restParams}
          error={error}
          onChange={(e) => {
            onChangeSearch(e.target.value);
          }}
          InputProps={{
            ...InputProps,
            type: 'search',
          }}
          placeholder={getPlaceholder()}
        />
      );
    },
    []
  );

  const value = useMemo(() => {
    if (selectedEntity === undefined || selectedEntity === null) {
      return multiple ? [] : nullOption;
    }

    return selectedEntity;
  }, [selectedEntity, multiple]);

  const handleOnChange = useCallback(
    (_: unknown, newValue: UuidableName | UuidableName[] | null): void => {
      if (multiple) {
        setSelectedEntity(newValue as UuidableName[]);
        updateField(field, newValue as UuidableName[]);
      } else {
        setSelectedEntity(newValue as UuidableName | null);
        updateField(field, newValue as UuidableName | null);
      }
    },
    [multiple, updateField, field]
  );

  const getPlaceholder = (): string => {
    const article = /^[aeiou]/i.test(singularKey) ? 'an' : 'a';
    return `Search or select ${article} ${singularKey}`;
  };

  return (
    <InfiniteAutocomplete
      loadingMore={loadingMore}
      hasMore={hasMore}
      onLoadMore={loadMore}
      disableCloseOnSelect={multiple}
      multiple={multiple}
      id={`${field}-dropdown-field-${module}`}
      options={results || []}
      value={value}
      renderOption={handleRenderOption}
      getOptionLabel={(option: SearchResponse) => option?.name as string}
      isOptionEqualToValue={(option, val) => option.uuid === val.uuid}
      getOptionDisabled={(option: SearchResponse) => option.uuid === loadingOption.uuid}
      renderInput={handleRenderInput}
      loading={loading}
      fullWidth
      disableClearable={!selectedEntity || (Array.isArray(selectedEntity) && selectedEntity.length === 0)}
      onChange={handleOnChange}
      onInputChange={(_, __, reason) => {
        if (reason === 'clear') {
          updateField(field, null);
        }
      }}
      renderTags={(tagValue, getTagProps) => {
        const chips = tagValue.map((option, index) => (
          <Chip {...getTagProps({ index })} label={option?.name || ''} fontSize={14} variant="filled" />
        ));
        return maxChips === undefined ? chips : limitChips(chips, maxChips);
      }}
    />
  );
};

export default EntitySearchField;
